/**
 * MobileTopBar.tsx — телефонная капсула оболочки кабинета (WEB-3739 юнит 2;
 * эталон `design/cabinet-rescin/app.jsx`: `.mob-top`, `.m-burger`, `.scrim`).
 *
 * До 640 px вместо меню слева — капсула сверху: знак, заголовок страницы
 * (подпись активного пункта меню, для вложенных адресов — родителя; на
 * `/settings` — «Личные настройки», на адресах конструктора — «Конструктор») и кнопка меню из двух черт (при открытом —
 * крестик). Меню (`Sidebar drawer`) выезжает шторкой сверху, под ним —
 * затемнение `.scrim`; закрыть — нажать по затемнению, свайпнуть меню влево
 * (сам `Sidebar`) или перейти куда угодно (`useShellDrawer`). Шире 640 капсулы и
 * затемнения нет (стили `styles/cabinet-shell.css`). Корень `.ds.mob-host` —
 * его соседом идёт `main` страницы (правило отступов `.ds.mob-host ~ .main`).
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MARK_SRC } from '@/components/brand/BrandLogo';
import { useAppSelector } from '@/hooks/hooks';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import { isEditorRoute } from '@/router/editorRoute';
import { useShellNav } from './Sidebar';
import { shellLabel } from './shellMenu';
import '../../styles/cabinet-shell.css';

/** Шторка меню на телефоне: открыта ли; любая смена адреса её закрывает. */
export function useShellDrawer(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [pathname, search]);
  return [open, setOpen];
}

export interface MobileTopBarProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export function MobileTopBar({ open, onToggle, onClose }: MobileTopBarProps) {
  const s = useStrings(commonStrings);
  const user = useAppSelector((state) => state.auth.user);
  const root = useCabinetRootAttributes(user?.theme);
  const { pathname } = useLocation();
  const { groups, activeKey } = useShellNav();
  const item = groups.flat().find((m) => m.key === activeKey);
  // WEB-3764 доводка холста: на адресах конструктора — «Конструктор» (эталон
  // sections/editor.jsx:11); признак — тот же, что у main.full оболочки.
  const title = isEditorRoute(pathname)
    ? s.builderTitle
    : item
      ? shellLabel(item, s)
      : pathname.startsWith('/settings')
        ? s.personalSettings
        : '';

  return (
    <div {...root} className={`${root.className} mob-host`}>
      <div className="mob-top">
        <img src={MARK_SRC} alt="" width="22" height="22" />
        <span className="mt-t">{title}</span>
        <button
          type="button"
          className="m-burger"
          aria-expanded={open}
          aria-label={s.shellOpenMenu}
          onClick={onToggle}
          data-testid="burger"
        >
          <i />
          <i />
        </button>
      </div>
      <div className={'scrim' + (open ? ' on' : '')} onClick={onClose} aria-hidden="true" />
    </div>
  );
}
